// Google Maps Service for displaying maps and handling directions
class GoogleMapsService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    this.mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
    this.isLoaded = false;
    this.isLoading = false;
    this.google = null;
    this.loadPromise = null;
  }

  // Load Google Maps API
  async loadGoogleMapsAPI() {
    // If already loaded, return the google object
    if (this.isLoaded && this.google) {
      return this.google;
    }

    // If currently loading, wait for the existing promise
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Check if Google Maps is already loaded globally
    if (window.google && window.google.maps) {
      this.google = window.google;
      this.isLoaded = true;
      return this.google;
    }

    // Check if API key is available
    if (!this.apiKey) {
      throw new Error('Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.');
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      // Wait for the existing script to load
      this.isLoading = true;
      this.loadPromise = new Promise((resolve, reject) => {
        const checkGoogle = () => {
          if (window.google && window.google.maps && window.google.maps.Map) {
            this.google = window.google;
            this.isLoaded = true;
            this.isLoading = false;
            resolve(this.google);
          } else {
            setTimeout(checkGoogle, 200); // Increased interval for better loading
          }
        };
        checkGoogle();
      });
      return this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = new Promise((resolve, reject) => {
      // Load Google Maps API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places,marker&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // Wait longer for Google Maps to fully initialize
        setTimeout(() => {
          if (window.google && window.google.maps && window.google.maps.Map) {
            this.google = window.google;
            this.isLoaded = true;
            this.isLoading = false;
            resolve(this.google);
          } else {
            reject(new Error('Google Maps failed to initialize properly'));
          }
        }, 1000); // Increased timeout to 1000ms for marker library
      };
      
      script.onerror = () => {
        this.isLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  // Initialize a map
  async initializeMap(element, options = {}) {
    const google = await this.loadGoogleMapsAPI();
    
    // Validate that Google Maps is properly loaded
    if (!google || !google.maps || !google.maps.Map) {
      throw new Error('Google Maps API is not properly loaded');
    }

    // Use fallback for MapTypeId if not available
    const mapTypeId = google.maps.MapTypeId?.ROADMAP || 'roadmap';
    
    const defaultOptions = {
      zoom: 15,
      center: { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
      mapTypeId: mapTypeId,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative', // Better touch handling
      mapId: this.mapId, // Required for Advanced Markers
      ...options
    };

    return new google.maps.Map(element, defaultOptions);
  }

  // Add a marker to the map using the new AdvancedMarkerElement
  async addMarker(map, position, options = {}) {
    const google = await this.loadGoogleMapsAPI();
    
    // Check if AdvancedMarkerElement is available (with timeout to prevent infinite loops)
    const checkAdvancedMarker = () => {
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 5; // Reduced from 10 to prevent long waits
        
        const check = () => {
          if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
            resolve(true);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(check, 200); // Increased delay to reduce frequency
          } else {
            resolve(false);
          }
        };
        
        check();
      });
    };

    const hasAdvancedMarker = await checkAdvancedMarker();
    
    if (hasAdvancedMarker) {
      try {
        const defaultOptions = {
          position,
          map,
          ...options
        };

        return new google.maps.marker.AdvancedMarkerElement(defaultOptions);
      } catch (error) {
        console.warn('AdvancedMarkerElement failed, falling back to traditional marker:', error);
      }
    }
    
    // Fallback to traditional marker if AdvancedMarkerElement is not available
    console.warn('Using deprecated google.maps.Marker - AdvancedMarkerElement not available');
    const defaultOptions = {
      position,
      map,
      animation: google.maps.Animation.DROP,
      ...options
    };

    return new google.maps.Marker(defaultOptions);
  }

  // Add an info window to a marker
  async addInfoWindow(map, marker, content) {
    const google = await this.loadGoogleMapsAPI();
    
    const infoWindow = new google.maps.InfoWindow({
      content
    });

    marker.addListener('click', () => {
      infoWindow.open(map, marker);
    });

    return infoWindow;
  }

  // Get directions between two points
  async getDirections(origin, destination, travelMode = 'DRIVING') {
    const google = await this.loadGoogleMapsAPI();
    
    const directionsService = new google.maps.DirectionsService();
    
    return new Promise((resolve, reject) => {
      directionsService.route(
        {
          origin,
          destination,
          travelMode: google.maps.TravelMode[travelMode]
        },
        (result, status) => {
          if (status === 'OK') {
            resolve(result);
          } else {
            reject(new Error(`Directions request failed due to ${status}`));
          }
        }
      );
    });
  }

  // Display directions on a map
  async displayDirections(map, origin, destination, travelMode = 'DRIVING') {
    const google = await this.loadGoogleMapsAPI();
    const directions = await this.getDirections(origin, destination, travelMode);
    
    const directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: false,
      polylineOptions: {
        strokeColor: '#817EFF',
        strokeWeight: 4
      }
    });

    directionsRenderer.setMap(map);
    directionsRenderer.setDirections(directions);

    return {
      directions,
      renderer: directionsRenderer
    };
  }

  // Find Place ID for an address using Places API
  async findPlaceId(address) {
    const google = await this.loadGoogleMapsAPI();
    
    if (!google.maps.places) {
      throw new Error('Places library not loaded');
    }
    
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    
    return new Promise((resolve, reject) => {
      const request = {
        query: address,
        fields: ['place_id', 'formatted_address', 'geometry']
      };
      
      service.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results.length > 0) {
          const place = results[0];
          resolve({
            place_id: place.place_id,
            formatted_address: place.formatted_address,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          });
        } else {
          reject(new Error(`Place search failed: ${status}`));
        }
      });
    });
  }

  // Geocode an address to get coordinates
  async geocodeAddress(geocodeInput) {
    const google = await this.loadGoogleMapsAPI();
    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode(geocodeInput, (results, status) => {
        if (status === 'OK') {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            formattedAddress: results[0].formatted_address,
            placeId: results[0].place_id
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }

  // Reverse geocode coordinates to get address
  async reverseGeocode(lat, lng) {
    const google = await this.loadGoogleMapsAPI();
    
    const geocoder = new google.maps.Geocoder();
    
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK') {
          resolve({
            address: results[0].formatted_address,
            components: results[0].address_components
          });
        } else {
          reject(new Error(`Reverse geocoding failed: ${status}`));
        }
      });
    });
  }

  // Open directions in external maps app
  openInMapsApp(origin, destination, travelMode = 'driving') {
    const originEncoded = encodeURIComponent(origin);
    const destinationEncoded = encodeURIComponent(destination);
    
    // Try to open in native maps app first, fallback to Google Maps web
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${originEncoded}&destination=${destinationEncoded}&travelmode=${travelMode}`;
    
    // For iOS, try to open in Apple Maps
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      const appleMapsUrl = `http://maps.apple.com/?saddr=${originEncoded}&daddr=${destinationEncoded}&dirflg=d`;
      window.open(appleMapsUrl, '_blank');
    } else {
      // For Android and other platforms, use Google Maps
      window.open(mapsUrl, '_blank');
    }
  }

  // Calculate distance between two points
  calculateDistance(point1, point2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.deg2rad(point2.lat - point1.lat);
    const dLon = this.deg2rad(point2.lng - point1.lng);
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(point1.lat)) * Math.cos(this.deg2rad(point2.lat)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in kilometers
    
    return distance;
  }

  // Convert degrees to radians
  deg2rad(deg) {
    return deg * (Math.PI/180);
  }

  // Format distance for display
  formatDistance(distance) {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    } else {
      return `${distance.toFixed(1)}km`;
    }
  }

  // Get estimated travel time
  async getTravelTime(origin, destination, travelMode = 'DRIVING') {
    try {
      const directions = await this.getDirections(origin, destination, travelMode);
      const route = directions.routes[0];
      const leg = route.legs[0];
      
      return {
        duration: leg.duration.text,
        distance: leg.distance.text,
        durationValue: leg.duration.value, // in seconds
        distanceValue: leg.distance.value // in meters
      };
    } catch (error) {
      console.error('Error getting travel time:', error);
      return null;
    }
  }
}

// Create and export a singleton instance
const googleMapsService = new GoogleMapsService();
export default googleMapsService; 